import { useEffect, useRef, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { Button } from '@higgins/ui';
import { getNearmapTile } from '../lib/api';
import { frameAround, rezoom, tileKey, tilesFor, type PlanFrame } from '../lib/site-plan-math';
import { Notice } from './message';

/** The plan's size in pixels: 4:3, like the iPad, at the tiles' own resolution. */
const PLAN_WIDTH = 1280;
const PLAN_HEIGHT = 960;
/** About 380 m across at Adelaide's latitude: a shopping centre and its car park. */
const START_ZOOM = 19;
const MIN_ZOOM = 15;
const MAX_ZOOM = 21;

/**
 * Frame a Nearmap plan: centred on where the iPad is, dragged to move and
 * zoomed with the buttons, then saved as it shows. Needs signal (tiles come
 * through the API, which holds the Nearmap key). Locked while saving (#470):
 * the save draws the frame as it was at the tap, so moving it then would only
 * make the screen differ from the plan saved.
 */
export function NearmapFramer({ at, saving, onSave }: { at: { latitude: number; longitude: number }; saving: boolean; onSave: (frame: PlanFrame, onScreen: Readonly<Record<string, string>>) => void }) {
  const [frame, setFrame] = useState<PlanFrame>(() => frameAround(at.latitude, at.longitude, START_ZOOM, PLAN_WIDTH, PLAN_HEIGHT));
  const [tiles, setTiles] = useState<Record<string, string>>({});
  const [missing, setMissing] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; left: number; top: number } | null>(null);
  const requested = useRef(new Set<string>());

  // Fetch the tiles in view that aren't here yet.
  useEffect(() => {
    for (const t of tilesFor(frame)) {
      const key = tileKey(t);
      if (requested.current.has(key)) continue;
      requested.current.add(key);
      getNearmapTile(t.z, t.x, t.y).then(
        (blob) => setTiles((all) => ({ ...all, [key]: URL.createObjectURL(blob) })),
        () => setMissing((n) => n + 1),
      );
    }
  }, [frame]);

  // Let go of the tile images when the framer closes.
  useEffect(() => () => Object.values(tiles).forEach((url) => URL.revokeObjectURL(url)), []); // eslint-disable-line react-hooks/exhaustive-deps -- once, on close

  /** Screen pixels → plan pixels: the plan is drawn scaled to the box's width. */
  const scale = () => (box.current ? box.current.clientWidth / PLAN_WIDTH : 1);

  function onPointerDown(e: React.PointerEvent) {
    if (saving) return;
    (e.target as Element).setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, left: frame.left, top: frame.top };
  }
  function onPointerMove(e: React.PointerEvent) {
    if (!drag.current) return;
    const s = scale();
    const { x, y, left, top } = drag.current;
    setFrame((f) => ({ ...f, left: Math.round(left - (e.clientX - x) / s), top: Math.round(top - (e.clientY - y) / s) }));
  }
  const onPointerUp = () => {
    drag.current = null;
  };

  return (
    <div className="space-y-2">
      <div
        ref={box}
        className="relative w-full touch-none overflow-hidden rounded-lg bg-slate-300"
        style={{ aspectRatio: `${PLAN_WIDTH} / ${PLAN_HEIGHT}` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {tilesFor(frame).map((t) => {
          const url = tiles[tileKey(t)];
          return url ? (
            <img
              key={tileKey(t)}
              src={url}
              alt=""
              draggable={false}
              className="absolute max-w-none select-none"
              style={{ left: `${(t.dx / PLAN_WIDTH) * 100}%`, top: `${(t.dy / PLAN_HEIGHT) * 100}%`, width: `${(256 / PLAN_WIDTH) * 100}%` }}
            />
          ) : null;
        })}
        <div className="pointer-events-none absolute left-1/2 top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-primary" title="You are here" />
        {saving && <div className="absolute inset-0 bg-white/40" aria-hidden />}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" onClick={() => setFrame((f) => rezoom(f, -1))} disabled={saving || frame.zoom <= MIN_ZOOM} aria-label="Zoom out">
          <Minus className="h-4 w-4" />
        </Button>
        <Button variant="outline" onClick={() => setFrame((f) => rezoom(f, 1))} disabled={saving || frame.zoom >= MAX_ZOOM} aria-label="Zoom in">
          <Plus className="h-4 w-4" />
        </Button>
        <span className="text-xs text-slate-500">Drag to move, zoom to frame the whole site. The dot is where you are.</span>
        <Button className="ml-auto" onClick={() => onSave(frame, tiles)} disabled={saving}>
          {saving ? 'Saving…' : 'Save this plan'}
        </Button>
      </div>
      {missing > 0 && <Notice>Nearmap has no imagery for part of this view; it stays grey on the plan.</Notice>}
    </div>
  );
}
